import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { CONTACT } from "./config";

export const openReservation = () => window.dispatchEvent(new CustomEvent("open-reservation"));

const TRIP_TYPES = [
  "Transfert aéroport / gare",
  "Longue distance",
  "Trajet local / mise à disposition",
];

const empty = { name: "", phone: "", datetime: "", pickup: "", dropoff: "", passengers: "1", type: TRIP_TYPES[0], notes: "" };

const field = "w-full rounded-xl bg-ink-700 border border-white/10 px-4 py-3 text-sm text-blush placeholder:text-muted-foreground/60 outline-none transition-colors focus:border-rose focus:ring-1 focus:ring-rose";
const labelCls = "mb-2 block text-[0.7rem] uppercase tracking-[0.16em] text-muted-foreground";

const ReservationDialog = () => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [error, setError] = useState("");

  useEffect(() => {
    const handler = () => { setOpen(true); setError(""); };
    window.addEventListener("open-reservation", handler);
    return () => window.removeEventListener("open-reservation", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.phone.trim() || !f.datetime || !f.pickup.trim() || !f.dropoff.trim()) {
      setError("Merci de renseigner les champs obligatoires (*).");
      return;
    }
    const dt = new Date(f.datetime).toLocaleString("fr-FR", { dateStyle: "full", timeStyle: "short" });
    const lines = [
      "Bonjour MN VTC, je souhaite réserver un trajet :",
      "",
      `• Nom : ${f.name}`,
      `• Téléphone : ${f.phone}`,
      `• Date & heure : ${dt}`,
      `• Départ : ${f.pickup}`,
      `• Arrivée : ${f.dropoff}`,
      `• Passagers : ${f.passengers}`,
      `• Type de trajet : ${f.type}`,
    ];
    if (f.notes.trim()) lines.push(`• Précisions : ${f.notes}`);
    const url = `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setOpen(false);
    setF(empty);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="reservation-dialog"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-ink/80 backdrop-blur-md" onClick={() => setOpen(false)} />

          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-rose/20 bg-ink-800 p-7 shadow-2xl sm:p-9"
          >
            <button data-testid="reservation-close" onClick={() => setOpen(false)} className="absolute right-5 top-5 text-muted-foreground transition-colors hover:text-rose">
              <X size={20} />
            </button>

            <p className="mb-2 text-xs uppercase tracking-[0.3em] text-rose">Réservation</p>
            <h3 className="mb-6 font-serif text-3xl tracking-tight text-blush">Composez votre trajet.</h3>

            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Nom complet *</label>
                  <input data-testid="res-name" className={field} value={f.name} onChange={set("name")} placeholder="Jean Dupont" />
                </div>
                <div>
                  <label className={labelCls}>Téléphone *</label>
                  <input data-testid="res-phone" type="tel" className={field} value={f.phone} onChange={set("phone")} placeholder="06 XX XX XX XX" />
                </div>
              </div>

              <div>
                <label className={labelCls}>Date & heure *</label>
                <input data-testid="res-datetime" type="datetime-local" className={`${field} [color-scheme:dark]`} value={f.datetime} onChange={set("datetime")} />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Adresse de départ *</label>
                  <input data-testid="res-pickup" className={field} value={f.pickup} onChange={set("pickup")} placeholder="Béziers, Gare SNCF..." />
                </div>
                <div>
                  <label className={labelCls}>Adresse d'arrivée *</label>
                  <input data-testid="res-dropoff" className={field} value={f.dropoff} onChange={set("dropoff")} placeholder="Aéroport de Montpellier..." />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Passagers</label>
                  <select data-testid="res-passengers" className={field} value={f.passengers} onChange={set("passengers")}>
                    {[1,2,3,4,5,6,7].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Type de trajet</label>
                  <select data-testid="res-type" className={field} value={f.type} onChange={set("type")}>
                    {TRIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelCls}>Précisions (facultatif)</label>
                <textarea data-testid="res-notes" rows={2} className={`${field} resize-none`} value={f.notes} onChange={set("notes")} placeholder="Bagages, vol, arrêt supplémentaire..." />
              </div>

              {error && <p data-testid="res-error" className="text-sm text-rose-light">{error}</p>}

              <button data-testid="res-submit" type="submit" className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-blush px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-ink transition-transform hover:scale-[1.02]">
                <FaWhatsapp size={18} /> Envoyer ma demande
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </button>
              <p className="text-center text-xs text-muted-foreground">Votre demande s'ouvre pré-remplie dans WhatsApp — il ne reste plus qu'à l'envoyer.</p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReservationDialog;
